import { type ColumnDef } from "@tanstack/react-table";
import { Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/empty-state";
import { StatusPill } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/lib/api";
import { useSetStaffActive, useStaffList } from "@/hooks/api/staff";
import type { User } from "@/types/api";

export default function StaffListPage() {
  const { data, isLoading, error } = useStaffList();
  const setActive = useSetStaffActive();
  const [pendingToggle, setPendingToggle] = useState<User | null>(null);

  const columns: ColumnDef<User>[] = [
    { accessorKey: "email", header: "Email" },
    { accessorKey: "display_name", header: "Name" },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) =>
        row.original.is_active ? (
          <StatusPill kind="success">Active</StatusPill>
        ) : (
          <StatusPill kind="danger">Deactivated</StatusPill>
        ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setPendingToggle(row.original)}
        >
          {row.original.is_active ? "Deactivate" : "Reactivate"}
        </Button>
      ),
    },
  ];

  const confirmToggle = async () => {
    if (!pendingToggle) return;
    try {
      await setActive.mutateAsync({
        id: pendingToggle.id,
        active: !pendingToggle.is_active,
      });
      toast.success(
        pendingToggle.is_active ? "Staff account deactivated." : "Staff account reactivated."
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't update that account."));
    } finally {
      setPendingToggle(null);
    }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <h1 className="m-0 text-2xl font-bold text-slate-900">Staff</h1>
        <Button asChild>
          <Link to="/staff/new">Add staff</Link>
        </Button>
      </div>

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={data?.results ?? []}
          loading={isLoading}
          error={error ? "Couldn't load staff accounts." : undefined}
          empty={
            <EmptyState
              icon={Users}
              title="No staff accounts yet"
              description="Add your first agent to give them access to published scripts."
              action={
                <Button asChild>
                  <Link to="/staff/new">Add staff</Link>
                </Button>
              }
            />
          }
        />
      </div>

      <ConfirmDialog
        isOpen={pendingToggle !== null}
        onClose={() => setPendingToggle(null)}
        onConfirm={confirmToggle}
        variant={pendingToggle?.is_active ? "warning" : "info"}
        title={pendingToggle?.is_active ? "Deactivate this account?" : "Reactivate this account?"}
        message={
          pendingToggle?.is_active
            ? `${pendingToggle?.email} will no longer be able to log in.`
            : `${pendingToggle?.email} will be able to log in again.`
        }
        confirmText={pendingToggle?.is_active ? "Deactivate" : "Reactivate"}
        isLoading={setActive.isPending}
      />
    </AppShell>
  );
}
